(() => {
  const TEXTBOOK_VARIANT_ID = 47968529121447;

  const DETECT_COURSE_BY_PROPERTIES = true;

  const COURSE_PROPERTY_KEYS = ["First Name", "Last Name", "Email Address"];

  const state = {
    enforcing: false,
    debounceTimer: null,
    pauseObserver: false, // prevents re-trigger during our own re-render
  };

  function log(...args) {
    console.log("[GLPTI Bundle]", ...args);
  }

  function isCartPage() {
    return location.pathname === "/cart" || location.pathname.startsWith("/cart");
  }

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return res.json();
  }

  async function changeByKey(lineItemKey, quantity) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: lineItemKey, quantity }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`cart/change.js failed: ${res.status} ${text}`);
    }
    return res.json();
  }

  function isTextbookItem(item) {
    return Number(item.id) === TEXTBOOK_VARIANT_ID || Number(item.variant_id) === TEXTBOOK_VARIANT_ID;
  }

  function looksLikeCourseItem(item) {
    if (isTextbookItem(item)) return false;

    if (!DETECT_COURSE_BY_PROPERTIES) return true;

    const props = item.properties || {};
    return COURSE_PROPERTY_KEYS.some((k) => Object.prototype.hasOwnProperty.call(props, k));
  }

  function sumCourseQty(cart) {
    return cart.items.reduce((sum, it) => (looksLikeCourseItem(it) ? sum + (it.quantity || 0) : sum), 0);
  }

  function findTextbook(cart) {
    return cart.items.find((it) => isTextbookItem(it)) || null;
  }

  // Dynamically resolve the Shopify section ID from a known inner DOM id.
  // e.g. inner div id="main-cart-footer" is inside
  //      id="shopify-section-template--XXXX__cart-footer"
  // The Sections API needs the part after "shopify-section-".
  function getSectionIdByInnerId(innerId) {
    const inner = document.getElementById(innerId);
    if (!inner) return null;
    // Walk up to find the shopify-section wrapper
    let el = inner.parentElement;
    while (el && el !== document.body) {
      if (el.id && el.id.startsWith("shopify-section-")) {
        return el.id.replace("shopify-section-", "");
      }
      el = el.parentElement;
    }
    return null;
  }

  // Re-render Dawn's cart section using the Sections API so UI updates instantly
  async function refreshCartSection() {
    try {
      state.pauseObserver = true;

      // Detect actual section IDs from the live DOM (handles template-specific IDs)
      const cartItemsSectionId = getSectionIdByInnerId("main-cart-items") || "main-cart-items";
      const cartFooterSectionId = getSectionIdByInnerId("main-cart-footer") || "main-cart-footer";
      log("Section IDs:", { cartItemsSectionId, cartFooterSectionId });

      const sectionsParam = [cartItemsSectionId, cartFooterSectionId, "cart-icon-bubble"].join(",");
      const response = await fetch(`${window.location.pathname}?sections=${sectionsParam}`);
      if (!response.ok) return;

      const data = await response.json();

      // Update main cart items section
      if (data[cartItemsSectionId]) {
        const parser = new DOMParser();
        const freshDoc = parser.parseFromString(data[cartItemsSectionId], "text/html");

        const freshContents = freshDoc.querySelector(".js-contents");
        const currentContents = document.querySelector("#main-cart-items .js-contents");

        if (freshContents && currentContents) {
          currentContents.innerHTML = freshContents.innerHTML;
          log("Cart items re-rendered via Sections API (.js-contents)");
        } else {
          const sectionEl = document.getElementById(`shopify-section-${cartItemsSectionId}`);
          const freshSection = freshDoc.getElementById(`shopify-section-${cartItemsSectionId}`);
          if (sectionEl && freshSection) {
            sectionEl.innerHTML = freshSection.innerHTML;
            log("Cart items re-rendered via Sections API (fallback)");
          }
        }
      }

      // Update cart footer — Estimated Total + checkout button
      if (data[cartFooterSectionId]) {
        const parser = new DOMParser();
        const freshDoc = parser.parseFromString(data[cartFooterSectionId], "text/html");

        const freshContents = freshDoc.querySelector(".js-contents");
        const currentContents = document.querySelector("#main-cart-footer .js-contents");

        if (freshContents && currentContents) {
          currentContents.innerHTML = freshContents.innerHTML;
          log("Cart footer re-rendered via Sections API (.js-contents)");
        } else {
          const sectionEl = document.getElementById(`shopify-section-${cartFooterSectionId}`);
          const freshSection = freshDoc.getElementById(`shopify-section-${cartFooterSectionId}`);
          if (sectionEl && freshSection) {
            sectionEl.innerHTML = freshSection.innerHTML;
            log("Cart footer re-rendered via Sections API (fallback)");
          }
        }
      }

      // Update cart bubble/icon count
      if (data["cart-icon-bubble"]) {
        const parser = new DOMParser();
        const freshDoc = parser.parseFromString(data["cart-icon-bubble"], "text/html");
        const sectionEl = document.querySelector("#shopify-section-cart-icon-bubble");
        const freshSection = freshDoc.querySelector("#shopify-section-cart-icon-bubble");
        if (sectionEl && freshSection) sectionEl.innerHTML = freshSection.innerHTML;
      }

      // Re-apply UI locks after new DOM nodes are injected, then resume observer
      setTimeout(() => {
        lockTextbookRowUI();
        state.pauseObserver = false;
      }, 50);

    } catch (e) {
      console.error("[GLPTI Bundle] refreshCartSection error:", e);
      state.pauseObserver = false;
    }
  }

  // Inject once — CSS handles link disabling even through re-renders
  function injectTextbookStyles() {
    if (document.getElementById("glpti-textbook-styles")) return;
    const style = document.createElement("style");
    style.id = "glpti-textbook-styles";
    style.textContent = `
      tr.glpti-textbook-row a,
      tr.glpti-textbook-row a:hover,
      tr.glpti-textbook-row a:focus,
      tr.glpti-textbook-row a:visited {
        pointer-events: none !important;
        cursor: default !important;
        text-decoration: none !important;
        color: inherit !important;
      }

      /* Gray out the entire textbook row */
      tr.glpti-textbook-row {
        opacity: 0.5 !important;
      }

      /* Keep the "Required with course" note fully visible */
      tr.glpti-textbook-row .glpti-textbook-locked-note {
        opacity: 1 !important;
        color: #555 !important;
      }
    `;
    document.head.appendChild(style);
    log("Textbook styles injected");
  }

  function lockTextbookRowUI() {
    if (!isCartPage()) return;

    injectTextbookStyles();

    const rows = document.querySelectorAll("tr.cart-item");
    rows.forEach((row) => {
      const qtyInput = row.querySelector(".quantity__input[data-quantity-variant-id]");
      const variantId = qtyInput ? Number(qtyInput.getAttribute("data-quantity-variant-id")) : null;

      if (variantId !== TEXTBOOK_VARIANT_ID) return;

      // Mark the row with a class — CSS above handles link styling automatically
      row.classList.add("glpti-textbook-row");

      // Capture the textbook URL before stripping href, so click blocker can use it
      row.querySelectorAll("a[href]").forEach((link) => {
        const href = link.getAttribute("href");
        if (href) {
          state_blockedUrls.add(href);
          log("Captured textbook URL:", href);
          link.dataset.glptiOriginalHref = href;
          link.removeAttribute("href");
        }
      });

      const qtyWrap =
        row.querySelector("quantity-input.quantity") ||
        row.querySelector(".cart-item__quantity-wrapper") ||
        row.querySelector(".quantity-popover-wrapper");

      if (qtyWrap) {
        const controls = qtyWrap.querySelector(".quantity-popover-container") || qtyWrap;
        if (controls) controls.style.display = "none";
      }

      const removeBtn = row.querySelector("cart-remove-button");
      if (removeBtn) removeBtn.style.display = "none";

      if (!row.querySelector(".glpti-textbook-locked-note")) {
        const note = document.createElement("div");
        note.className = "glpti-textbook-locked-note";
        note.style.marginTop = "6px";
        note.style.fontSize = "14px";
        note.style.fontWeight = "600";
        note.textContent = "Required with course";

        const nameEl = row.querySelector(".cart-item__name");
        if (nameEl && nameEl.parentElement) {
          nameEl.parentElement.insertBefore(note, nameEl.nextSibling);
        }
      }

      const qtyCell = row.querySelector("td.cart-item__quantity");
      if (qtyCell && !qtyCell.querySelector(".glpti-textbook-locked-qty")) {
        const msg = document.createElement("div");
        msg.className = "glpti-textbook-locked-qty";
        msg.style.opacity = "0.75";
        msg.style.fontSize = "14px";
        msg.textContent = "Included with your course (required).";
        qtyCell.appendChild(msg);
      }
    });
  }

  async function enforcePairing() {
    if (!isCartPage()) return;
    if (state.enforcing) return;

    state.enforcing = true;
    let madeChange = false;

    try {
      const cart = await getCart();

      const textbook = findTextbook(cart);
      const courseQty = sumCourseQty(cart);
      const textbookQty = textbook ? textbook.quantity : 0;

      log({ courseQty, textbookQty });

      // Rule A: ebook cannot exist alone -> remove ebook
      if (courseQty === 0) {
        if (textbook) {
          log("No course found, removing ebook");
          await changeByKey(textbook.key, 0);
          madeChange = true;
        }
      }
      // Rule B: ebook qty must match course qty
      else if (textbook && textbookQty !== courseQty) {
        log(`Sync ebook qty ${textbookQty} -> ${courseQty}`);
        await changeByKey(textbook.key, courseQty);
        madeChange = true;
      }
      // Rule C: course exists but textbook missing
      else if (!textbook) {
        log("Course exists but ebook missing, adding ebook");
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: TEXTBOOK_VARIANT_ID, quantity: courseQty }] }),
        });
        madeChange = true;
      }

      // If we changed anything, refresh the cart UI immediately (no page reload needed)
      if (madeChange) {
        log("Change made — refreshing cart section UI...");
        await refreshCartSection();
      }

    } catch (e) {
      console.error("[GLPTI Bundle] enforcePairing error:", e);
    } finally {
      state.enforcing = false;
    }
  }

  function debounceRun() {
    // Don't react to DOM mutations caused by our own re-render
    if (state.pauseObserver) return;

    clearTimeout(state.debounceTimer);
    state.debounceTimer = setTimeout(async () => {
      lockTextbookRowUI();
      await enforcePairing();
      setTimeout(lockTextbookRowUI, 200);
    }, 120);
  }

  function observeCartDom() {
    if (!isCartPage()) return;

    const target =
      document.querySelector("#main-cart-items") ||
      document.querySelector("cart-items") ||
      document.body;

    const obs = new MutationObserver(() => debounceRun());
    obs.observe(target, { childList: true, subtree: true });
  }

  function patchFetchForCartCalls() {
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      try {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
        if (
          url.includes("/cart/change") ||
          url.includes("/cart/update") ||
          url.includes("/cart/add")
        ) {
          setTimeout(debounceRun, 150);
        }
      } catch (_) {}
      return res;
    };
  }

  // Store the textbook product URL once we find it in the DOM
  const state_blockedUrls = new Set();

  function captureTextbookUrl() {
    // Find the textbook row by variant ID on the qty input
    const rows = document.querySelectorAll("tr.cart-item");
    rows.forEach((row) => {
      const qtyInput = row.querySelector(".quantity__input[data-quantity-variant-id]");
      const variantId = qtyInput ? Number(qtyInput.getAttribute("data-quantity-variant-id")) : null;
      if (variantId !== TEXTBOOK_VARIANT_ID) return;

      // Grab all href values from this row and store them
      row.querySelectorAll("a[href]").forEach((link) => {
        const href = link.getAttribute("href");
        if (href) {
          state_blockedUrls.add(href);
          log("Captured textbook URL to block:", href);
        }
      });
    });
  }

  // Block ALL clicks on any link pointing to a blocked URL — capture phase,
  // fires before Dawn's own handlers, works regardless of class or DOM state
  function installClickBlocker() {
    document.addEventListener("click", (e) => {
      const link = e.target.closest("a");
      if (!link) return;

      // Check by blocked URL set
      const href = link.getAttribute("href") || link.dataset.glptiOriginalHref || "";
      if (href && state_blockedUrls.has(href)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        log("Blocked textbook link by URL:", href);
        return;
      }

      // Fallback: check by row class (belt and suspenders)
      const row = link.closest("tr.glpti-textbook-row");
      if (row) {
        e.preventDefault();
        e.stopImmediatePropagation();
        log("Blocked textbook link by row class");
      }
    }, true); // capture phase — fires before ANY other handler
  }

  // Show an inline error message above the checkout button
  function showCartError(message) {
    // Remove any existing error
    const existing = document.getElementById("glpti-checkout-error");
    if (existing) existing.remove();

    const el = document.createElement("div");
    el.id = "glpti-checkout-error";
    el.style.cssText = `
      background: #fff0f0;
      border: 1px solid #ED002E;
      color: #ED002E;
      padding: 12px 16px;
      margin-bottom: 12px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 600;
      text-align: center;
    `;
    el.textContent = message;

    // Insert above the checkout button
    const checkoutBtn = document.querySelector("[name='checkout'], a[href='/checkout'], button[type='submit']");
    if (checkoutBtn && checkoutBtn.parentElement) {
      checkoutBtn.parentElement.insertBefore(el, checkoutBtn);
    } else {
      // Fallback: append to cart footer
      const footer = document.getElementById("main-cart-footer");
      if (footer) footer.prepend(el);
    }

    // Auto-dismiss after 6 seconds
    setTimeout(() => el.remove(), 6000);
  }

  // Intercept checkout — validate cart before allowing navigation
  function installCheckoutGuard() {
    document.addEventListener("click", async (e) => {
      const btn = e.target.closest("[name='checkout'], a[href='/checkout'], button[type='submit']");
      if (!btn) return;

      // Only intercept the main checkout button, not other submit buttons
      const isCheckout =
        btn.getAttribute("name") === "checkout" ||
        btn.getAttribute("href") === "/checkout" ||
        btn.closest("form[action='/cart']");
      if (!isCheckout) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      log("Checkout intercepted — validating cart...");

      try {
        const cart = await getCart();
        const courseQty = sumCourseQty(cart);
        const textbook = findTextbook(cart);

        // Block: ebook in cart but no course
        if (textbook && courseQty === 0) {
          log("Checkout blocked — ebook without course");

          // Remove the stray ebook
          await changeByKey(textbook.key, 0);
          await refreshCartSection();

          showCartError("The E-Textbook can only be purchased as part of a course. Please register for a course to proceed.");
          return;
        }

        // Block: no items at all
        if (cart.item_count === 0 || courseQty === 0) {
          showCartError("Your cart is empty. Please add a course to proceed.");
          return;
        }

        // Cart is valid — proceed to checkout
        log("Cart valid — proceeding to checkout");
        window.location.href = "/checkout";

      } catch (err) {
        console.error("[GLPTI Bundle] Checkout guard error:", err);
        // On error, don't block — let them through
        window.location.href = "/checkout";
      }
    }, true); // capture phase
  }

  // ── EBOOK PRODUCT PAGE GUARD ──────────────────────────────────────────────
  // If someone lands directly on the ebook product page, redirect them away.
  // The ebook can only be purchased as part of a course bundle.
  function isTextbookProductPage() {
    // Matches /products/certification-e-textbook or any variant URL with
    // the textbook variant ID in the query string
    const path = location.pathname.toLowerCase();
    const params = new URLSearchParams(location.search);
    const variantParam = Number(params.get("variant"));

    return (
      variantParam === TEXTBOOK_VARIANT_ID ||
      path.includes("certification-e-textbook") ||
      path.includes("e-textbook") ||
      path.includes("etextbook")
    );
  }

  function handleTextbookProductPage() {
    if (!isTextbookProductPage()) return;

    log("Ebook product page detected — redirecting...");

    // Replace the page content immediately so user doesn't see flash of product
    document.documentElement.style.visibility = "hidden";

    // Redirect to courses page (update this path if needed)
    window.location.replace("/");
  }

  // ── ACCELERATED CHECKOUT BUTTON GUARD (Shop Pay, PayPal etc on cart) ──────
  // Hide "Buy with Shop" / dynamic payment buttons when cart has no valid course.
  // These buttons skip the cart page and bypass our checkout guard.
  async function manageAcceleratedButtons() {
    if (!isCartPage()) return;

    try {
      const cart = await getCart();
      const courseQty = sumCourseQty(cart);
      const textbook = findTextbook(cart);

      // If ebook exists without a course, hide ALL accelerated checkout buttons
      const soloEbook = textbook && courseQty === 0;

      // Selectors covering Shop Pay, PayPal, Apple Pay, Google Pay buttons in Dawn
      const acceleratedSelectors = [
        ".shopify-payment-button",
        ".dynamic-checkout",
        "shop-pay-button",
        "[data-shopify='payment-button']",
        ".cart__dynamic-checkout-buttons",
        "#dynamic-checkout-cart",
      ];

      const allBtns = document.querySelectorAll(acceleratedSelectors.join(","));

      allBtns.forEach((el) => {
        if (soloEbook) {
          el.style.display = "none";
          el.dataset.glptiHidden = "true";
          log("Hidden accelerated button (solo ebook in cart)");
        } else if (el.dataset.glptiHidden) {
          el.style.display = "";
          delete el.dataset.glptiHidden;
        }
      });

    } catch (e) {
      console.error("[GLPTI Bundle] manageAcceleratedButtons error:", e);
    }
  }

  function init() {
    if (!isCartPage()) return;

    patchFetchForCartCalls();
    observeCartDom();
    captureTextbookUrl();   // capture URL before href is stripped
    installClickBlocker();
    installCheckoutGuard();

    lockTextbookRowUI();
    enforcePairing();
    manageAcceleratedButtons();
    setTimeout(lockTextbookRowUI, 300);
    setTimeout(manageAcceleratedButtons, 500);
  }

  // ── HIDE EBOOK FROM COLLECTION PAGES ─────────────────────────────────────
  function hideTextbookFromCollection() {
    if (!location.pathname.startsWith("/collections")) return;

    function doHide() {
      // Cast a wide net across all Dawn card wrapper variations
      const allCards = document.querySelectorAll(
        ".card-wrapper, .product-card-wrapper, li.grid__item, .grid__item"
      );
      allCards.forEach((card) => {
        const text = card.textContent.toLowerCase();
        if (text.includes("e-textbook")) {
          card.style.display = "none";
          log("Hidden ebook card from collection:", card);
        }
      });
    }

    // Run immediately and also after a short delay in case Dawn renders late
    doHide();
    setTimeout(doHide, 500);
    setTimeout(doHide, 1500);
  }

  document.addEventListener("DOMContentLoaded", () => {
    // Handle ebook product page redirect first (runs on ALL pages)
    handleTextbookProductPage();

    // Hide ebook from collection listing
    hideTextbookFromCollection();

    // Then run cart-specific logic
    init();
  });
  document.addEventListener("shopify:section:load", () => debounceRun());
})();
